import { useState, useEffect, useCallback } from 'react';
import emergencyAPI from '../services/emergencyAPI';
import type { EmergencyStatus } from '../types/emergency';
import { useAppSelector } from '../store/hooks';

export interface UseEmergencyReturn {
  status: EmergencyStatus | null;
  loading: boolean;
  error: string | null;
  refreshStatus: () => Promise<void>;
  isEmergencyActive: boolean;
  emergencyLevel: string;
  maintenanceMode: boolean;
}

export const useEmergency = (autoRefresh = false, refreshInterval = 60000): UseEmergencyReturn => {
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const [status, setStatus] = useState<EmergencyStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshStatus = useCallback(async () => {
    if (!isAuthenticated) {
      setStatus(null);
      setLoading(false);
      return;
    }

    try {
      setError(null);
      const response = await emergencyAPI.getPublicStatus();
      setStatus({
        ...response,
        active_blocks: {},
        blocked_users_count: 0,
        blocked_ips_count: 0,
      });
    } catch {
      // Transient DB/network issues — fail silently for banner polling
      setError(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      setStatus(null);
      return;
    }
    setLoading(true);
    refreshStatus();
  }, [isAuthenticated, refreshStatus]);

  useEffect(() => {
    if (!autoRefresh || !isAuthenticated) return undefined;

    const interval = setInterval(() => {
      refreshStatus();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, refreshStatus, isAuthenticated]);

  const isEmergencyActive = status?.is_emergency_active || false;
  const emergencyLevel = status?.emergency_level || 'normal';
  const maintenanceMode = status?.maintenance_mode || false;

  return {
    status,
    loading,
    error,
    refreshStatus,
    isEmergencyActive,
    emergencyLevel,
    maintenanceMode,
  };
};

export default useEmergency;
